// GanttOprawaConnections.jsx
import React, { useMemo } from 'react';

const PALETA_KOLOROW = [
    '#3f51b5', // Indygo
    '#00bcd4', // Cyjan
    '#f4a261', // Pomarańczowy
    '#9c27b0', // Fioletowy
    '#8bc34a', // Jasnozielony
  '#e63946', // Czerwony
  '#2a9d8f', // Turkusowy
  '#e91e63', // Różowy
  '#ff9800', // Głęboki pomarańcz
  '#795548', // Brązowy
];

const KOLOR_DOMYSLNY = '#888888';

// Sprawdza, czy dany element_id znajduje się w ciągu tekstowym "1,2,3"
const zawieraElement = (elementyIdText, searchElementId) => {
  if (!elementyIdText || searchElementId === undefined || searchElementId === null) return false;
  const lista = String(elementyIdText).split(',').map(s => s.trim()).filter(Boolean);
  return lista.includes(String(searchElementId));
};

const GanttOprawaConnections = ({
  groupedStages,
  processorIds,
  rowYPositions,
  minDate,
  maxDate,
  totalChartWidth,
  selectedZamowienieId,
  showOprawaConnections, // Stan włączający ten typ połączeń
  calculateBarProperties
}) => {
  const connectionLines = useMemo(() => {
    // Rysujemy tylko wtedy, gdy tryb jest włączony i mamy wybrane zamówienie
    if (!showOprawaConnections || !selectedZamowienieId) return [];
    if (!minDate || !maxDate || !totalChartWidth) return [];

    // 1. Zbieramy wszystkie etapy dla wybranego zamówienia
    const targetStages = [];

    processorIds.forEach((processorId) => {
      const rowStages = groupedStages[processorId] || [];
      const y = rowYPositions[processorId] || 0;

      rowStages.forEach((stage) => {
        if (stage.zamowienie_id === selectedZamowienieId) {
          const { leftPx, widthPx } = calculateBarProperties(stage.start, stage.end);
          targetStages.push({
            ...stage,
            stageId: stage.global_id || stage.id,
            indeks: Number(stage.indeks_procesu) || 0,
            procesNazwaId: Number(stage.proces_nazwa_id),
            outPoint: { x: leftPx + widthPx, y },
            inPoint: { x: leftPx, y }
          });
        }
      });
    });

    if (targetStages.length === 0) return [];

    // 2. Szukamy dla każdego element_id etapu o MAX(indeks_procesu)
    const maxStageByElement = {};

    targetStages.forEach((stage) => {
      if (!stage.element_id) return;
      
      const elemId = stage.element_id;
      if (
        !maxStageByElement[elemId] ||
        stage.indeks > maxStageByElement[elemId].indeks
      ) {
        maxStageByElement[elemId] = stage;
      }
    });

    // Przypisanie kolorów dla poszczególnych element_id
    const elementColorMap = {};
    Object.keys(maxStageByElement).forEach((elemId, index) => {
      elementColorMap[elemId] = index < PALETA_KOLOROW.length
        ? PALETA_KOLOROW[index]
        : KOLOR_DOMYSLNY;
    });

    // 3. Znajdujemy etapy Oprawy (proces_nazwa_id === 6)
    const oprawaStages = targetStages.filter(stage => stage.procesNazwaId === 6);

    const lines = [];

    // 4. Łączymy ostatni etap każdego element_id z odpowiednim etapem oprawy
    Object.keys(maxStageByElement).forEach((elemId) => {
      const lastStageOfElement = maxStageByElement[elemId];

      // Szukamy etapów oprawy, które zawierają ten element_id w polu elementy_id (np. "1,2,3")
      const matchingOprawy = oprawaStages.filter(oprawaStage =>
        zawieraElement(oprawaStage.elementy_id, elemId)
      );

      matchingOprawy.forEach((oprawaStage) => {
        const strokeColor = elementColorMap[elemId] || KOLOR_DOMYSLNY;

        lines.push({
          id: `oprawa_${elemId}_${lastStageOfElement.stageId}_to_${oprawaStage.stageId}`,
          elementId: elemId,
          from: lastStageOfElement.outPoint,
          to: oprawaStage.inPoint,
          strokeColor
        });
      });
    });

    return lines;
  }, [
    groupedStages,
    processorIds,
    rowYPositions,
    minDate,
    maxDate,
    totalChartWidth,
    selectedZamowienieId,
    showOprawaConnections,
    calculateBarProperties
  ]);

  if (connectionLines.length === 0) return null;

  const uniqueElements = Array.from(
    new Map(connectionLines.map(line => [line.elementId, line.strokeColor])).entries()
  );

  return (
    <svg
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 11
      }}
    >
      <defs>
        {uniqueElements.map(([elementId, color]) => (
          <marker
            key={`marker-oprawa-${elementId}`}
            id={`arrowhead-oprawa-${elementId}`}
            markerWidth="6"
            markerHeight="6"
            refX="5"
            refY="3"
            orient="auto"
          >
            <polygon points="0 0, 6 3, 0 6" fill={color} />
          </marker>
        ))}
      </defs>

      {connectionLines.map((line) => {
        const { from, to, id, strokeColor, elementId } = line;
        const deltaX = Math.max(20, Math.abs(to.x - from.x) / 2);
        const pathData = `M ${from.x} ${from.y} C ${from.x + deltaX} ${from.y}, ${to.x - deltaX} ${to.y}, ${to.x} ${to.y}`;

        return (
          <g key={`connection-oprawa-${id}`}>
            <path
              d={pathData}
              fill="none"
              stroke={strokeColor}
              strokeWidth="4.5"
              strokeDasharray="6 2" // Inny wzór kreskowania do odróżnienia linii oprawy
              markerEnd={`url(#arrowhead-oprawa-${elementId})`}
            />
            <circle cx={from.x} cy={from.y} r="3.5" fill={strokeColor} />
          </g>
        );
      })}
    </svg>
  );
};

export default React.memo(GanttOprawaConnections);